import React, { useState, useEffect } from 'react';
import api from '../api';
import Table from '../components/Table';
import Modal from '../components/Modal';
import ImageUpload from '../components/ImageUpload';
import { FiPlus, FiTrash2, FiCalendar, FiMapPin, FiUser, FiTag, FiUsers, FiLink, FiImage } from 'react-icons/fi';
import toast from 'react-hot-toast';

const LinkInput = ({ links, setLinks }) => {
  const addLink = () => {
    setLinks([...links, { title: '', url: '' }]);
  };

  const updateLink = (index, field, value) => {
    const updatedLinks = links.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    );
    setLinks(updatedLinks);
  };

  const removeLink = (index) => {
    setLinks(links.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-2">
      {links.map((link, index) => (
        <div key={index} className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Title"
            value={link.title}
            onChange={(e) => updateLink(index, 'title', e.target.value)}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          <input
            type="url"
            placeholder="URL"
            value={link.url}
            onChange={(e) => updateLink(index, 'url', e.target.value)}
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
          <button type="button" onClick={() => removeLink(index)} className="text-red-600 hover:text-red-800">
            <FiTrash2 />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addLink}
        className="mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Add Link
      </button>
    </div>
  );
};
const FormGroup = ({ children, cols = 1 }) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
  };
  return (
    <div className={`grid ${colClasses[cols]} gap-6 mb-6`}>
      {children}
    </div>
  );
};

const InputField = ({ icon, label, name, type = 'text', value, onChange, required = true }) => (
  <div>
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <div className="mt-1 relative rounded-md shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {icon}
      </div>
      <input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
        required={required}
      />
    </div>
  </div>
);
const Events = () => {
  const [events, setEvents] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    location: '',
    organizer: '',
    tags: [],
    images: [],
    speakers: [],
    guests: [],
    links: [],
    colleges: [],
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchEvents();
    fetchColleges();
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/event', {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm,
        },
      });
      setEvents(response.data.data);
      setPagination(prevState => ({
        ...prevState,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages,
      }));
    } catch (error) {
      toast.error('Failed to fetch events');
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await api.get('/college');
      setColleges(response.data.data);
    } catch (error) {
      toast.error('Failed to fetch colleges');
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prevState => ({ ...prevState, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prevState => ({ ...prevState, limit: newLimit, page: 1 }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination(prevState => ({ ...prevState, page: 1 }));
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleArrayInputChange = (e, field) => {
    const values = e.target.value.split(',').map(item => item.trim());
    setFormData({ ...formData, [field]: values });
  };

  const handleImageUpload = (urls) => {
    setFormData(prevState => ({
      ...prevState,
      images: [...prevState.images, ...urls]
    }));
  };

  const handleImageRemove = (index) => {
    setFormData(prevState => ({
      ...prevState,
      images: prevState.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (currentEvent) {
        await api.put(`/event/${currentEvent._id}`, data);
        toast.success('Event updated successfully');
      } else {
        await api.post('/event', data);
        toast.success('Event created successfully');
      }
      setIsModalOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleEdit = (event) => {
    setCurrentEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      startDate: event.startDate.split('T')[0],
      endDate: event.endDate.split('T')[0],
      location: event.location,
      organizer: event.organizer,
      tags: event.tags,
      images: event.images,
      speakers: event.speakers,
      guests: event.guests,
      links: event.links,
      colleges: event.colleges ? event.colleges.map(c => c._id) : (event.college ? [event.college._id] : []),
    });
    setIsModalOpen(true);
  };

  const handleCollegeToggle = (collegeId) => {
    setFormData(prevState => {
      const isSelected = prevState.colleges.includes(collegeId);
      if (isSelected) {
        return { ...prevState, colleges: prevState.colleges.filter(id => id !== collegeId) };
      } else {
        return { ...prevState, colleges: [...prevState.colleges, collegeId] };
      }
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await api.delete(`/event/${id}`);
        toast.success('Event deleted successfully');
        fetchEvents();
      } catch (error) {
        toast.error('Failed to delete event');
      }
    }
  };

  const columns = [
    { key: 'title', label: 'Title' },
    { key: 'startDate', label: 'Start Date', render: (item) => new Date(item.startDate).toLocaleDateString() },
    { key: 'endDate', label: 'End Date', render: (item) => new Date(item.endDate).toLocaleDateString() },
    { key: 'location', label: 'Location' },
    { key: 'colleges', label: 'Colleges', render: (item) => item.colleges ? item.colleges.map(c => c.name).join(', ') : (item.college ? item.college.name : '-') },
  ];

 

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Events</h1>
        <button
          onClick={() => {
            setCurrentEvent(null);
            setFormData({
              title: '',
              description: '',
              startDate: '',
              endDate: '',
              location: '',
              organizer: '',
              tags: [],
              images: [],
              speakers: [],
              guests: [],
              links: [],
              colleges: [],
            });
            setIsModalOpen(true);
          }}
          className="px-4 py-2 font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
        >
          <FiPlus className="inline-block mr-2" />
          Add Event
        </button>
      </div>
      <Table
        columns={columns}
        data={events}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSearch={handleSearch}
      />
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={currentEvent ? 'Edit Event' : 'Add Event'} size="xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormGroup cols={2}>
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiMapPin className="h-5 w-5 text-gray-400" />}
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                id="description"
                name="description"
                rows="3"
                value={formData.description}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              ></textarea>
            </div>
          </FormGroup>
          <FormGroup cols={2}>
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="Start Date"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="End Date"
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup cols={2}>
            <InputField
              icon={<FiUser className="h-5 w-5 text-gray-400" />}
              label="Organizer"
              name="organizer"
              value={formData.organizer}
              onChange={handleInputChange}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Colleges</label>
              <div className="mt-1 border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                {colleges.length === 0 ? (
                  <p className="text-gray-500 text-sm">No colleges available</p>
                ) : (
                  <div className="space-y-2">
                    {colleges.map((college) => (
                      <label key={college._id} className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={formData.colleges.includes(college._id)}
                          onChange={() => handleCollegeToggle(college._id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">{college.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {formData.colleges.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">{formData.colleges.length} college(s) selected</p>
              )}
            </div>
          </FormGroup>
          <FormGroup cols={3}>
            <InputField
              icon={<FiTag className="h-5 w-5 text-gray-400" />}
              label="Tags (comma-separated)"
              name="tags"
              value={formData.tags.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'tags')}
            />
            <InputField
              icon={<FiUsers className="h-5 w-5 text-gray-400" />}
              label="Speakers (comma-separated)"
              name="speakers"
              value={formData.speakers.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'speakers')}
            />
            <InputField
              icon={<FiUsers className="h-5 w-5 text-gray-400" />}
              label="Guests (comma-separated)"
              name="guests"
              value={formData.guests.join(', ')}
              onChange={(e) => handleArrayInputChange(e, 'guests')}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Links</label>
              <LinkInput links={formData.links} setLinks={(links) => setFormData({ ...formData, links })} />
            </div>
          </FormGroup>
          <FormGroup>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Images</label>
              <ImageUpload
                onUpload={handleImageUpload}
                onRemove={handleImageRemove}
                images={formData.images}
                multiple
              />
            </div>
          </FormGroup>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {currentEvent ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Events;